'use client';

import React, { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { apiClient, User, LoginResponse } from '@/lib/api-client';
import { disconnectSocket, updateSocketAuth } from '@/socket';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string, username?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Add a small delay to ensure localStorage is accessible in incognito mode
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const token = localStorage.getItem('auth_token');
        console.log('[Auth] Checking auth with token:', token ? 'exists' : 'none');
        
        if (token) {
          apiClient.setToken(token);
          const response = await apiClient.getMe();
          setUser(response.user);
          console.log('[Auth] Successfully authenticated user:', response?.user?.id);
          
          // Initialize socket connection with JWT token
          console.log('[Auth] Initializing socket for user:', response?.user?.id);
          updateSocketAuth(token);
        } else {
          console.log('[Auth] No token found, user will remain unauthenticated');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Only clear token if it's actually invalid (not just expired or network error)
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('invalid') || errorMessage.includes('malformed') || errorMessage.includes('401')) {
          console.log('[Auth] Invalid token detected, clearing auth');
          localStorage.removeItem('auth_token');
          apiClient.clearToken();
          // Clear cookie
          document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
          setUser(null);
        } else {
          console.log('[Auth] Auth check failed but token might still be valid, not clearing. Error:', errorMessage);
        }
      } finally {
        // Always set loading to false, even if auth check fails
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response: LoginResponse = await apiClient.login(email, password);
      
      // Ensure token is set in API client and localStorage
      apiClient.setToken(response.token);
      // Also set cookie for middleware
      document.cookie = `auth_token=${response.token}; path=/; max-age=${7 * 24 * 60 * 60}`; // 7 days
      setUser(response.user);
      
      // Initialize socket connection with JWT token
      console.log('[Auth] Login successful, initializing socket for user:', response?.user?.id);
      updateSocketAuth(response.token);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, name?: string, username?: string) => {
    try {
      setLoading(true);
      const response: LoginResponse = await apiClient.register(email, password, name, username);
      
      // Ensure token is set in API client and localStorage
      apiClient.setToken(response.token);
      // Also set cookie for middleware
      document.cookie = `auth_token=${response.token}; path=/; max-age=${7 * 24 * 60 * 60}`; // 7 days
      setUser(response.user);
      
      // Initialize socket connection with JWT token
      console.log('[Auth] Registration successful, initializing socket for user:', response?.user?.id);
      updateSocketAuth(response.token);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log('[Auth] Logging out, disconnecting socket');
    disconnectSocket();
    setUser(null);
    localStorage.removeItem('auth_token');
    // Clear cookie
    document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    apiClient.clearToken();
  };

  const refreshUser = async () => {
    try {
      const response = await apiClient.getMe();
      setUser(response.user);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser
  };

  return React.createElement(
    AuthContext.Provider,
    { value },
    children
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Compatibility hook to replace useSession
export function useSession() {
  const { user, loading } = useAuth();
  
  return {
    data: user ? { user } : null,
    status: loading ? 'loading' : user ? 'authenticated' : 'unauthenticated'
  };
}